import React, {useEffect, useMemo, useState} from 'react';
import {NavLink} from "react-router-dom";
import {PlusIcon} from "@heroicons/react/24/solid";
import Loader from "../ui/Loader";
import {categoryApi} from "../../api/CategoriesService";
import {TransactionType} from "../../model/TransactionType";
import {ICategory, ICategoryUsage} from "../../model/ICategory";
import {classNames} from "../../utils/common";
import NewCategoryForm from "./NewCategoryForm";
import CategoryTreeItem from "./CategoryTreeItem";
import DeleteCategoryDialog from "./DeleteCategoryDialog";
import {
    categoryHasChildren,
    CategorySnapshot,
    DropPlacement,
    getApiErrorMessage,
    getDropPlacement,
    resolveDrop,
    snapshotCategory,
} from "./categoryTreeUtils";
import {useDragAutoScroll} from "./useDragAutoScroll";

interface CategoryTreeProps {
    trType: TransactionType;
}

const CategoryTree: React.FunctionComponent<CategoryTreeProps> = ({trType}) => {
    const {data: categories, isLoading, error} = categoryApi.useFetchAllCategoriesQuery(trType);
    const [updateCategory] = categoryApi.useUpdateCategoryMutation();
    const [moveCategoryMutation] = categoryApi.useMoveCategoryMutation();
    const [fetchUsage] = categoryApi.useLazyFetchCategoryUsageQuery();

    const [showNewCategoryForm, setShowNewCategoryForm] = useState(false);
    const [highlightedCategoryId, setHighlightedCategoryId] = useState<number | null>(null);
    const [draggingId, setDraggingId] = useState<number | null>(null);
    const [dropTarget, setDropTarget] = useState<{id: number | 'root', placement: DropPlacement} | null>(null);
    const [treeError, setTreeError] = useState('');
    const [categoryToDelete, setCategoryToDelete] = useState<ICategory | null>(null);
    const [deleteUsage, setDeleteUsage] = useState<ICategoryUsage | null>(null);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [lastChange, setLastChange] = useState<CategorySnapshot | null>(null);

    const allCategories = useMemo(() => categories ?? [], [categories]);
    useDragAutoScroll(draggingId != null);

    useEffect(() => {
        if (highlightedCategoryId == null) {
            return;
        }
        const frame = window.requestAnimationFrame(() => {
            const row = document.querySelector(`[data-category-id="${highlightedCategoryId}"]`) as HTMLElement | null;
            if (!row) {
                return;
            }
            row.scrollIntoView({behavior: 'smooth', block: 'nearest'});
            row.focus({preventScroll: true});
        });
        return () => window.cancelAnimationFrame(frame);
    }, [highlightedCategoryId, allCategories]);

    async function handleRename(category: ICategory, name: string): Promise<boolean> {
        const snapshot = snapshotCategory(allCategories, category);
        try {
            await updateCategory({trType, id: category.id, name}).unwrap();
            setLastChange(snapshot);
            setHighlightedCategoryId(category.id);
            setTreeError('');
            return true;
        } catch (err) {
            setTreeError(getApiErrorMessage(err, 'Could not rename category.'));
            return false;
        }
    }

    async function handleDelete(category: ICategory) {
        if (categoryHasChildren(allCategories, category.id)) {
            setTreeError('Move or delete child categories first.');
            return;
        }
        try {
            const usage = await fetchUsage({trType, id: category.id}).unwrap();
            setCategoryToDelete(category);
            setDeleteUsage(usage);
            setDeleteOpen(true);
            setTreeError('');
        } catch (err) {
            setTreeError(getApiErrorMessage(err, 'Could not load category usage.'));
        }
    }

    async function applyMove(categoryId: number, parentId: number | null, position: number) {
        const category = allCategories.find((item) => item.id === categoryId);
        if (!category) {
            return;
        }
        const snapshot = snapshotCategory(allCategories, category);
        try {
            await moveCategoryMutation({trType, id: categoryId, parent_id: parentId, position}).unwrap();
            setLastChange(snapshot);
            setHighlightedCategoryId(category.id);
            setTreeError('');
        } catch (err) {
            setTreeError(getApiErrorMessage(err, 'Could not move category.'));
        }
    }

    async function handleRevert() {
        if (!lastChange) {
            return;
        }
        try {
            await updateCategory({trType, id: lastChange.id, name: lastChange.name}).unwrap();
            await moveCategoryMutation({
                trType,
                id: lastChange.id,
                parent_id: lastChange.parent_id,
                position: lastChange.position,
            }).unwrap();
            setLastChange(null);
            setHighlightedCategoryId(lastChange.id);
            setTreeError('');
        } catch (err) {
            setTreeError(getApiErrorMessage(err, 'Could not revert category.'));
        }
    }

    function handleDragStart(category: ICategory) {
        setDraggingId(category.id);
        setDropTarget(null);
    }

    function handleDragEnd() {
        setDraggingId(null);
        setDropTarget(null);
    }

    function handleDragOverCategory(category: ICategory, event: React.DragEvent<HTMLTableRowElement>) {
        if (draggingId == null) {
            return;
        }
        const placement = getDropPlacement(event);
        const move = resolveDrop(allCategories, draggingId, category, placement);
        if (!move) {
            setDropTarget((current) => current?.id === category.id ? null : current);
            return;
        }
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
        setDropTarget({id: category.id, placement});
    }

    function handleDropOnCategory(category: ICategory, event: React.DragEvent<HTMLTableRowElement>) {
        event.preventDefault();
        const sourceId = draggingId ?? Number(event.dataTransfer.getData('text/plain'));
        const placement = getDropPlacement(event);
        setDraggingId(null);
        setDropTarget(null);
        if (!sourceId) {
            return;
        }
        const move = resolveDrop(allCategories, sourceId, category, placement);
        if (!move) {
            return;
        }
        applyMove(sourceId, move.parentId, move.position);
    }

    function handleDragOverRoot(event: React.DragEvent<HTMLDivElement>) {
        if (draggingId == null) {
            return;
        }
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
        setDropTarget({id: 'root', placement: 'into'});
    }

    function handleDropOnRoot(event: React.DragEvent<HTMLDivElement>) {
        event.preventDefault();
        const sourceId = draggingId ?? Number(event.dataTransfer.getData('text/plain'));
        setDraggingId(null);
        setDropTarget(null);
        if (!sourceId) {
            return;
        }
        const source = allCategories.find((item) => item.id === sourceId);
        const rootCount = allCategories.filter((item) => item.parent_id == null && item.id !== sourceId).length;
        const alreadyLastRoot = source?.parent_id == null
            && allCategories.filter((item) => item.parent_id == null).findIndex((item) => item.id === sourceId) === rootCount;
        if (alreadyLastRoot) {
            return;
        }
        applyMove(sourceId, null, rootCount);
    }

    return (
        <>
            <div className="bg-white px-3 py-2 flex items-center border-t border-gray-200 sm:px-4">
                <p className="text-xs text-gray-700 mr-2">
                    Type
                </p>
                <div className="inline-flex" role="tablist" aria-label="Category type">
                    <NavLink
                        to="/model/expense"
                        role="tab"
                        className={({isActive}) => classNames(
                            isActive
                                ? 'bg-gray-300 text-indigo-900'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200',
                            'px-3 py-1 text-xs font-medium rounded-l-full'
                        )}
                    >
                        Expense
                    </NavLink>
                    <NavLink
                        to="/model/income"
                        role="tab"
                        className={({isActive}) => classNames(
                            isActive
                                ? 'bg-gray-300 text-indigo-900'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200',
                            'px-3 py-1 text-xs font-medium rounded-r-full'
                        )}
                    >
                        Income
                    </NavLink>
                </div>
                <button
                    type="button"
                    title="Add category"
                    aria-label="Add category"
                    onClick={() => setShowNewCategoryForm((visible) => !visible)}
                    className={classNames(
                        showNewCategoryForm
                            ? "bg-green-200 text-green-900"
                            : "bg-green-100 text-green-800 hover:bg-green-200",
                        "ml-auto inline-flex items-center justify-center rounded-full p-1.5 focus:outline-none focus:ring-2 focus:ring-green-500"
                    )}
                >
                    <PlusIcon className="h-4 w-4"/>
                </button>
            </div>
            <NewCategoryForm
                show={showNewCategoryForm}
                trType={trType}
                categories={allCategories}
                onClose={() => setShowNewCategoryForm(false)}
                onCreated={(categoryId) => {
                    setHighlightedCategoryId(categoryId);
                    setLastChange(null);
                }}
            />
            <div
                onDragOver={handleDragOverRoot}
                onDragLeave={() => {
                    if (dropTarget?.id === 'root') {
                        setDropTarget(null);
                    }
                }}
                onDrop={handleDropOnRoot}
                className={classNames(
                    dropTarget?.id === 'root' ? 'border-indigo-400 bg-indigo-50' : 'border-gray-200',
                    'mx-3 my-2 rounded-md border border-dashed px-3 py-2 text-xs text-gray-500'
                )}
            >
                Drop here to make a root category
            </div>
            {treeError && (
                <div className="mx-3 mb-2 text-xs text-red-700">{treeError}</div>
            )}
            {isLoading && <div className="flex justify-center"><Loader/></div>}
            {error && <div>Error occurred during fetching categories</div>}
            <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                <tr>
                    <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Category
                    </th>
                    <th scope="col" className="px-3 py-2">
                        <span className="sr-only">Revert</span>
                    </th>
                    <th scope="col" className="relative px-3 py-2">
                        <span className="sr-only">Actions</span>
                    </th>
                </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                {allCategories.map((category) =>
                    <CategoryTreeItem
                        key={category.id}
                        category={category}
                        dropPlacement={dropTarget?.id === category.id ? dropTarget.placement : null}
                        isDragging={draggingId === category.id}
                        highlighted={highlightedCategoryId === category.id}
                        showRevert={lastChange?.id === category.id}
                        onDragStart={handleDragStart}
                        onDragOver={handleDragOverCategory}
                        onDrop={handleDropOnCategory}
                        onDragEnd={handleDragEnd}
                        onRename={handleRename}
                        onDelete={handleDelete}
                        onRevert={handleRevert}
                    />
                )}
                </tbody>
            </table>
            {allCategories.length === 0 && !isLoading && (
                <div className="flex justify-center text-base text-gray-700 py-3">No categories</div>
            )}
            <DeleteCategoryDialog
                open={deleteOpen}
                trType={trType}
                category={categoryToDelete}
                usage={deleteUsage}
                categories={allCategories}
                onClose={() => {
                    setDeleteOpen(false);
                    setCategoryToDelete(null);
                    setDeleteUsage(null);
                }}
                onDeleted={() => {
                    if (lastChange && categoryToDelete && lastChange.id === categoryToDelete.id) {
                        setLastChange(null);
                    }
                    setHighlightedCategoryId(null);
                }}
            />
        </>
    );
};

export default CategoryTree;
